import { Link, createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { Button } from "@ghezaa/ui";
import { NotFound, scrollOnNewPage } from "@ghezaa/shell";
import { Shell } from "./components/shell";
import { MarketEntry, MarketGate } from "./lib/market";
import { BusinessCentralPage } from "./routes/business-central";
import { CustomerPage } from "./routes/customer";
import { CustomersPage, customersSearch } from "./routes/customers";
import { DataGapsPage } from "./routes/data-gaps";
import { LogPage, logSearch } from "./routes/log";
import { MarketsPage, marketsSearch } from "./routes/markets";
import { PeoplePage, peopleSearch } from "./routes/people";
import { NewReportPage, SavedReportPage, reportSearch } from "./routes/report";
import { DefinitionsPage, ReportsPage } from "./routes/reports";
import { TargetsPage, targetsSearch } from "./routes/targets";
import { TeamPage, teamSearch } from "./routes/team";
import { TodayPage } from "./routes/today";

function SalesNotFound() {
  return (
    <NotFound
      action={
        <Button asChild>
          <Link to="/">Open Sales</Link>
        </Button>
      }
    />
  );
}

const rootRoute = createRootRoute({ component: Shell, notFoundComponent: SalesNotFound });
// `/` opens the market this browser used last (Core's link to Sales lands here).
const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: MarketEntry });
const marketsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/markets", component: MarketsPage, validateSearch: marketsSearch });
const logRoute = createRoute({ getParentRoute: () => rootRoute, path: "/log", component: LogPage, validateSearch: logSearch });

// Every other page is in a market, and the address says which: /eg/customers. The code is upper case in code.
const marketRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$market",
  params: {
    parse: ({ market }) => ({ market: market.toUpperCase() }),
    stringify: ({ market }) => ({ market: market.toLowerCase() }),
  },
  component: MarketGate,
});
const todayRoute = createRoute({ getParentRoute: () => marketRoute, path: "/today", component: TodayPage });
const teamRoute = createRoute({ getParentRoute: () => marketRoute, path: "/team", component: TeamPage, validateSearch: teamSearch });
const targetsRoute = createRoute({ getParentRoute: () => marketRoute, path: "/targets", component: TargetsPage, validateSearch: targetsSearch });
const reportsRoute = createRoute({ getParentRoute: () => marketRoute, path: "/reports", component: ReportsPage });
const newReportRoute = createRoute({ getParentRoute: () => marketRoute, path: "/reports/new", component: NewReportPage, validateSearch: reportSearch });
const definitionsRoute = createRoute({ getParentRoute: () => marketRoute, path: "/reports/definitions", component: DefinitionsPage });
const reportRoute = createRoute({ getParentRoute: () => marketRoute, path: "/reports/$id", component: SavedReportPage, validateSearch: reportSearch });
const dataGapsRoute = createRoute({ getParentRoute: () => marketRoute, path: "/data-gaps", component: DataGapsPage });
const customersRoute = createRoute({ getParentRoute: () => marketRoute, path: "/customers", component: CustomersPage, validateSearch: customersSearch });
const customerRoute = createRoute({ getParentRoute: () => marketRoute, path: "/customers/$no", component: CustomerPage });
const peopleRoute = createRoute({ getParentRoute: () => marketRoute, path: "/people", component: PeoplePage, validateSearch: peopleSearch });
const businessCentralRoute = createRoute({ getParentRoute: () => marketRoute, path: "/business-central", component: BusinessCentralPage });

const routeTree = rootRoute.addChildren([
  indexRoute,
  marketsRoute,
  logRoute,
  marketRoute.addChildren([todayRoute, teamRoute, targetsRoute, reportsRoute, newReportRoute, definitionsRoute, reportRoute, customersRoute, customerRoute, dataGapsRoute, peopleRoute, businessCentralRoute]),
]);

export const router = createRouter({ routeTree, scrollRestoration: scrollOnNewPage(), defaultPreload: "intent", defaultNotFoundComponent: SalesNotFound });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
